import React, { useState, useCallback, useMemo } from "react";
import OperationalProgressBar from "./OperationalProgressBar";
import OperationalServiceCardCollapse from "./OperationalServiceCardCollapse";
import { navigation, Spinner } from "nr1";
import { useAccount } from "./account-context";
import { SKIP_NAVIGATION_ALERTS } from "./constants/healthEndpoints";
import Scrollable from "./shared/Scrollable";
/* =========================================================
   Group issues by ALERT → STORE → DEVICES
   ========================================================= */
function groupByAlertAndStore(stores = [], serviceName) {
  const alertMap = {};

  if (!Array.isArray(stores)) return [];

  stores.forEach((store) => {
    const issues = store?.services?.[serviceName] || [];

    issues.forEach((issue) => {
      const alert = issue.alert || "Unknown Alert";
      const storeId = store.store;

      if (!alertMap[alert]) {
        alertMap[alert] = {};
      }

      if (!alertMap[alert][storeId]) {
        alertMap[alert][storeId] = {
          store: storeId,
          identifiers: [],
          guid: issue.guid,
        };
      }

      if (issue.detail) {
        alertMap[alert][storeId].identifiers.push(issue.detail);
      }
    });
  });

  // return Object.entries(alertMap).map(([alert, storesMap]) => ({
  //   alert,
  //   count: Object.keys(storesMap).length, // ✅ unique store count
  //   stores: Object.values(storesMap),
  // }));

  return Object.entries(alertMap).map(([alert, storesMap]) => {
    const storesArray = Object.values(storesMap);

    //  SORT BY STORE NUMBER
    storesArray.sort((a, b) => a.store.localeCompare(b.store));

    return {
      alert,
      count: storesArray.length,
      stores: storesArray,
    };
  });
}

/* =========================================================
   OPERATIONAL SERVICE CARD
   ========================================================= */
function OperationalServiceCard({
  name,
  percent,
  color,
  note,
  storesData = [],
  onClick,
}) {
  const { accountId } = useAccount();
  const [parentOpen, setParentOpen] = useState(false);
  const [openAlerts, setOpenAlerts] = useState({}); // ✅ per‑alert open state
  const [loading] = useState(false);

  /* -------------------------
     Build alert groups
     ------------------------- */
  const alertGroups = useMemo(
    () => groupByAlertAndStore(storesData, name),
    [storesData, name],
  );

  /* -------------------------
     Toggle alert open/close
     ------------------------- */
  const toggleAlert = (alertName) => {
    setOpenAlerts((prev) => ({
      ...prev,
      [alertName]: !prev[alertName],
    }));
  };

  const serviceKey = (name || "").toUpperCase();

  /* -------------------------
     Can a row be opened?

     Drives both the click handler and the pointer cursor, so the affordance
     cannot promise a navigation the handler then refuses.
     ------------------------- */
  const canOpenRow = useCallback(
    (storeId, alert) =>
      Boolean(storeId) &&
      Boolean(serviceKey) &&
      serviceKey !== "MYSTORE" &&
      !SKIP_NAVIGATION_ALERTS.includes(alert),
    [serviceKey],
  );

  /* -------------------------
     Row click handler
     ------------------------- */
  const handleRowClick = useCallback(
    (entityGuid, storeId, serviceName, alert) => {
      if (!canOpenRow(storeId, alert)) return;

      let guid = entityGuid;

      //  Generate GUID if missing
      if (entityGuid === "NOGUID") {
        guid = btoa(`${accountId}|EXT|${serviceName}|${storeId}`).replaceAll(
          "=",
          "",
        );
      }

      // console.log("Row clicked with:", { storeId, serviceName, alert, accountId });
      //       const guid = btoa(
      //         `${accountId}|EXT|${serviceName}|${storeId}`,
      //       ).replaceAll("=", "");

      navigation.openStackedEntity(guid, {
        // Scope the dashboard to this store.
        selectedVariables: { entity: storeId },
        // The service this row is grouped under. It has to be passed: the
        // guid the health API attaches to an alert is stamped
        // DIGITALORDERING whatever the service is, so a Sesame row opened
        // the Digital Ordering dashboard when the panel had only the guid
        // to go on.
        serviceName,
      });
    },
    [accountId, canOpenRow],
  );

  return (
    <div
      className={`opsc-card opsc-service-card${
        onClick ? " opsc-clickable" : ""
      }`}
      onClick={(e) => {
        if (
          e.target.closest(".opsc-collapse-container") ||
          e.target.closest(".opsc-issue-row") ||
          e.target.closest(".opsc-alert-title")
        ) {
          return;
        }
        onClick?.();
      }}
    >
      {/* =========================
         HEADER
         ========================= */}
      <div className="opsc-header">
        <span>{name}</span>
        <span>{percent}%</span>
      </div>

      <OperationalProgressBar percent={percent} color={color} />

      {/* =========================
         ALERTS SECTION
         ========================= */}
      {alertGroups.length > 0 ? (
        <OperationalServiceCardCollapse
          title={note}
          isParent
          open={parentOpen}
          setOpen={setParentOpen}
        >
          <div className="opsc-collapse-container">
            {alertGroups.map((grp) => {
              const isOpen = !!openAlerts[grp.alert];

              return (
                <Scrollable key={grp.alert} className="opsc-alert-block">
                  {/* ALERT HEADER (click to toggle) */}
                  <div
                    className="opsc-alert-title"
                    onClick={() => toggleAlert(grp.alert)}
                  >
                    <strong className="opsc-alert-text">
                      {grp.alert} ({grp.count})
                    </strong>

                    <span
                      className={`opsc-alert-arrow ${
                        openAlerts[grp.alert] ? "opsc-open" : ""
                      }`}
                    >
                      ▾
                    </span>
                  </div>

                  {/* STORE ROWS (shown ONLY when alert is open) */}
                  {isOpen &&
                    grp.stores.map((item) => (
                      <div
                        key={item.store}
                        className={`opsc-issue-row opsc-issue-text${
                          canOpenRow(item.store, grp.alert)
                            ? " opsc-clickable"
                            : ""
                        }`}
                        onClick={() =>
                          handleRowClick(
                            item.guid || "NOGUID",
                            item.store,
                            serviceKey,
                            grp.alert,
                          )
                        }
                      >
                        <strong>{item.store}</strong> –{" "}
                        {item.identifiers.join(", ")}
                      </div>
                    ))}
                </Scrollable>
              );
            })}
          </div>
        </OperationalServiceCardCollapse>
      ) : (
        <small className="opsc-small">{note}</small>
      )}

      {loading && <Spinner />} 
    </div>
  );
}

export default OperationalServiceCard;

// import React, { useState, useEffect, useCallback } from "react";
// import OperationalProgressBar from "./OperationalProgressBar";
// import OperationalServiceCardCollapse from "./OperationalServiceCardCollapse";
// import { navigation, Spinner } from "nr1";
// import { useAccount } from "./account-context";
// import { groupIssuesByStoreAndAlert } from "./buildServiceItems";

// function OperationalServiceCard({
//   name,
//   percent,
//   color,
//   note,
//   digitalDetailGroups = {},
//   onClick,
// }) {
//   const [loading, setLoading] = useState(false);
//   const [parentOpen, setParentOpen] = useState(false);
//   const { accountId } = useAccount();

//   const groupKeys = Object.keys(digitalDetailGroups);

//   /** -------------------------
//    * Row click handler
//    * ------------------------- */
//   const handleRowClick = useCallback(
//     (storeId, serviceName) => {
//       if (serviceName === "MYSTORE") return;

//       const guid = btoa(
//         `${accountId}|EXT|${serviceName}|${storeId}`
//       ).replaceAll("=", "");

//       navigation.openStackedEntity(guid);
//     },
//     [accountId]
//   );

//   /** -------------------------
//    * Parent toggle logic
//    * ------------------------- */
//   const toggleParent = (isOpen) => {
//     setParentOpen(isOpen);
//   };

//   return (
//     <div
//       className={`oh-card oh-service-card ${
//         name === "MyStore" ? "no-cursor" : "clickable"
//       }`}
//       onClick={(e) => {
//         if (
//           e.target.closest(".servicecard-collapse-container") ||
//           e.target.closest(".service-issue-row")
//         ) {
//           return;
//         }
//         onClick?.();
//       }}
//     >
//       {/* Header */}
//       <div className="oh-service-header">
//         <span>{name}</span>
//         <span>{percent}%</span>
//       </div>

//       <OperationalProgressBar percent={percent} color={color} />

//       {/* Parent Collapse */}
//       {groupKeys.length > 0 ? (
//         <OperationalServiceCardCollapse
//           title={note}
//           isParent
//           open={parentOpen}
//           setOpen={toggleParent}
//         >
//           {/*  INLINE, GROUPED, NO CHILD COLLAPSE */}
//           {/* {Object.values(digitalDetailGroups).map((grp) => {
//             const groupedRows = groupIssuesByStoreAndAlert(grp);

//             return groupedRows.map((row) => {
//               const hasIds = row.identifiers?.length > 0;

//               return (
//                 <div
//                   key={`${row.store}-${grp.alert}`}
//                   className="service-issue-row issue-text"
//                   title={
//                     hasIds
//                       ? row.identifiers.join(", ")
//                       : `${row.count} issue(s)`
//                   }
//                   onClick={() =>
//                     handleRowClick(
//                       row.store,
//                       (name || "").toUpperCase()
//                     )
//                   }
//                 >
//                   <strong>{row.store}</strong> – {row.alert}
//                   <span className="service-issue-summary">
//                     {hasIds
//                       ? ` (${row.identifiers.join(", ")})`
//                       : ` (${row.count} issue${
//                           row.count > 1 ? "s" : ""
//                         })`}
//                   </span>
//                 </div>
//               );
//             });
//           })} */}

//           {Object.values(digitalDetailGroups).map((grp) => (
//             <OperationalServiceCardCollapse
//               key={grp.alert}
//               title={`${grp.alert} (${grp.count})`}
//               open={childOpenStates[grp.alert]}
//               setOpen={(val) =>
//                 setChildOpenStates((prev) => ({
//                   ...prev,
//                   [grp.alert]: val,
//                 }))
//               }
//             >
//               {grp.stores.map((item, index) => {
//                 const entityGuid = item?.guid ?? "NOGUID";

//                 return (
//                   <div
//                     className="issue-row issue-text"
//                     key={`${item.store}-${index}`}
//                     onClick={() => handleRowClick(entityGuid, item.store,   (name || "").toUpperCase(), accountId)}
//                   >
//                     {item.store} - {item.detail}
//                   </div>
//                 );
//               })}
//             </OperationalServiceCardCollapse>
//           ))}
//         </OperationalServiceCardCollapse>
//       ) : (
//         <small className="oh-small">
//           {name !== "MyStore" ? note : name}
//         </small>
//       )}

//       {loading && <Spinner />}
//     </div>
//   );
// }

// export default OperationalServiceCard;

// import React, { useState, useEffect, useCallback } from "react";
// import OperationalProgressBar from "./OperationalProgressBar";
// import OperationalServiceCardCollapse from "./OperationalServiceCardCollapse";
// import { navigation, nerdlet,Spinner } from "nr1";
// import { useAccount } from "./account-context";
// import  {groupIssuesByStoreAndAlert} from "./buildServiceItems";
// function OperationalServiceCard({
//   name,
//   percent,
//   color,
//   note,
//   digitalDetailGroups = {},
//   onClick,
// }) {
//   const [loading, setLoading] = useState(false);
//   const [parentOpen, setParentOpen] = useState(false);
//   const [childOpenStates, setChildOpenStates] = useState({});
//   const { accountId } = useAccount();
//   const groupKeys = Object.keys(digitalDetailGroups);

//   /** -------------------------
//    * Row click handler
//    * ------------------------- */
//   const handleRowClick = useCallback((entityGuid, storeId, serviceName, accountId) => {
//       if (serviceName === "MYSTORE") {
//         return;
//       }
//       //console.log("Row clicked with:", { entityGuid, storeId, serviceName,accountId });
//       let guid = btoa(`${accountId}|EXT|${serviceName}|${storeId}`).replaceAll(
//         "=",
//         "",
//       );
//     // nerdlet.setConfig({ timePickerDefaultOffset: 86400000 });
//     navigation.openStackedEntity(guid);
//   }, []);

//   /** -------------------------
//    * Initialize child collapse state
//    * ------------------------- */
//   useEffect(() => {
//     const init = {};
//     groupKeys.forEach((key) => (init[key] = false));
//     setChildOpenStates(init);
//   }, [groupKeys.length]);

//   /** -------------------------
//    * Parent toggle logic
//    * ------------------------- */
//   const toggleParent = (isOpen) => {
//     setParentOpen(isOpen);

//     if (!isOpen) {
//       const collapsed = {};
//       groupKeys.forEach((key) => (collapsed[key] = false));
//       setChildOpenStates(collapsed);
//     }
//   };
// console.log("Rendering OperationalServiceCard with:", digitalDetailGroups );
//   return (
//     <div
//       className={`oh-card oh-service-card ${
//         name === "MyStore" ? "no-cursor" : "clickable"
//       }`}
//       onClick={(e) => {
//         if (
//           e.target.closest(".servicecard-collapse-container") ||
//           e.target.closest(".issue-row .issue-text")
//         ) {
//           return;
//         }
//         onClick?.();
//       }}
//     >
//       {/* Header */}
//       <div className="oh-service-header">
//         <span>{name}</span>
//         <span>{percent}%</span>
//       </div>

//       <OperationalProgressBar percent={percent} color={color} />

//       {/* Parent / Child Collapsible */}
//       {groupKeys.length > 0 ? (
//         <OperationalServiceCardCollapse
//           title={note}
//            isParent={true}
//           open={parentOpen}
//           setOpen={toggleParent}
//         >
//           {Object.values(digitalDetailGroups).map((grp) => (
//             <OperationalServiceCardCollapse
//               key={grp.alert}
//               title={`${grp.alert} (${grp.count})`}
//               open={childOpenStates[grp.alert]}
//               setOpen={(val) =>
//                 setChildOpenStates((prev) => ({
//                   ...prev,
//                   [grp.alert]: val,
//                 }))
//               }
//             >
//               {grp.stores.map((item, index) => {
//                 const entityGuid = item?.guid ?? "NOGUID";

//                 return (
//                   <div
//                     className="issue-row issue-text"
//                     key={`${item.store}-${index}`}
//                     onClick={() => handleRowClick(entityGuid, item.store,   (name || "").toUpperCase(), accountId)}
//                   >
//                     {item.store} - {item.detail}
//                   </div>
//                 );
//               })}
//             </OperationalServiceCardCollapse>
//           ))}
//           {/* {Object.values(digitalDetailGroups).map((grp) => {
//   const groupedRows = groupIssuesByStoreAndAlert(grp);

//   return groupedRows.map((row) => (
//     <div
//       key={`${row.store}-${grp.alert}`}
//       className="issue-row issue-text"
//       title={row.devices.join(", ")} // hover shows full list
//       onClick={() =>
//         handleRowClick(
//           null,
//           row.store,
//           (name || "").toUpperCase(),
//           accountId
//         )
//       }
//     >
//       <strong>{row.store}</strong> – {row.alert}
//       <span className="issue-devices">
//         ({row.devices.join(", ")})
//       </span>
//     </div>
//   ));
// })} */}
//         </OperationalServiceCardCollapse>
//       ) : (
//         <small className="oh-small">
//           {name !== "MyStore" ? note : name}
//         </small>
//       )}

//       {loading && <Spinner />}
//     </div>
//   );
// }

// export default OperationalServiceCard;
